/**
 * ContainerEditorSheet — bottom-sheet editor for a single schedule container.
 * AddContainerPicker — bottom-sheet type picker for inserting a new container.
 *
 * Leaf components: props only, no infrastructure imports. (SOLID-01)
 */
import { useState } from "react";
import type { BellPatternId } from "@retreat/core";
import type { ContainerTypeMeta } from "@retreats/content";
import styles from "../screens/ScheduleEditorScreen.module.css";

// ── Constants ─────────────────────────────────────────────────────────────────

const DURATION_STEP_S = 300;
const MIN_DURATION_S = 300;
export const MAX_CONTAINER_NAME_LENGTH = 80;

// ── Helpers ───────────────────────────────────────────────────────────────────

export function formatDuration(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatBellLabel(p: BellPatternId): string {
  return p
    .replace(/_single$|_double$|_bell$/, "")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

function haptic() {
  try { navigator.vibrate(10); } catch { /* unsupported */ }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type EditorContainer = {
  id: string;
  type: string;
  name: string;
  durationSeconds: number;
  bellBeforePattern: BellPatternId | null;
  bellAfterPattern: BellPatternId | null;
};

// ── DurationStepper ───────────────────────────────────────────────────────────

function DurationStepper({
  seconds,
  onChange,
}: {
  seconds: number;
  onChange: (s: number) => void;
}) {
  return (
    <div className={styles.stepper}>
      <button
        type="button"
        className={styles.stepBtn}
        onClick={() => { haptic(); onChange(Math.max(MIN_DURATION_S, seconds - DURATION_STEP_S)); }}
        aria-label="Decrease duration"
      >
        −
      </button>
      <span className={styles.stepVal}>{formatDuration(seconds)}</span>
      <button
        type="button"
        className={styles.stepBtn}
        onClick={() => { haptic(); onChange(seconds + DURATION_STEP_S); }}
        aria-label="Increase duration"
      >
        +
      </button>
    </div>
  );
}

// ── BellPillRow ───────────────────────────────────────────────────────────────

function BellPillRow({
  label,
  value,
  patterns,
  onChange,
}: {
  label: string;
  value: BellPatternId | null;
  patterns: BellPatternId[];
  onChange: (p: BellPatternId | null) => void;
}) {
  return (
    <div className={styles.pillSection}>
      <span className={styles.pillSectionLabel}>{label}</span>
      <div className={styles.pillRow}>
        <button
          type="button"
          className={`${styles.pill} ${value === null ? styles.pillActive : ""}`}
          onClick={() => { haptic(); onChange(null); }}
        >
          None
        </button>
        {patterns.map((p) => (
          <button
            key={p}
            type="button"
            className={`${styles.pill} ${value === p ? styles.pillActive : ""}`}
            onClick={() => { haptic(); onChange(p); }}
          >
            {formatBellLabel(p)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── TypePillRow ───────────────────────────────────────────────────────────────

function TypePillRow({
  value,
  types,
  onChange,
}: {
  value: string;
  types: ContainerTypeMeta[];
  onChange: (t: string) => void;
}) {
  return (
    <div className={styles.pillSection}>
      <span className={styles.pillSectionLabel}>Type</span>
      <div className={styles.pillRow}>
        {types.map((ct) => (
          <button
            key={ct.type}
            type="button"
            className={`${styles.pill} ${value === ct.type ? styles.pillActive : ""}`}
            onClick={() => { haptic(); onChange(ct.type); }}
          >
            {ct.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── ContainerEditorSheet ──────────────────────────────────────────────────────

export type ContainerEditorSheetProps = {
  container: EditorContainer;
  bellPatterns: BellPatternId[];
  containerTypes: ContainerTypeMeta[];
  onUpdate: (c: EditorContainer) => void;
  onDelete: () => void;
  onClose: () => void;
};

export function ContainerEditorSheet({
  container,
  bellPatterns,
  containerTypes,
  onUpdate,
  onDelete,
  onClose,
}: ContainerEditorSheetProps) {
  const [local, setLocal] = useState<EditorContainer>(container);
  const set = <K extends keyof EditorContainer>(k: K, v: EditorContainer[K]) =>
    setLocal((p) => ({ ...p, [k]: v }));

  return (
    <div className={styles.sheetOverlay} onClick={onClose}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.sheetHandle} />
        <div className={styles.sheetBody}>
          <label className={styles.fieldLabel}>
            Name
            <input
              className={styles.fieldInput}
              value={local.name}
              onChange={(e) => set("name", e.target.value)}
              maxLength={MAX_CONTAINER_NAME_LENGTH}
            />
          </label>
          <TypePillRow
            value={local.type}
            types={containerTypes}
            onChange={(t) => {
              const oldLabel = containerTypes.find((ct) => ct.type === local.type)?.label ?? local.type;
              const newLabel = containerTypes.find((ct) => ct.type === t)?.label ?? t;
              setLocal((p) => ({
                ...p,
                type: t,
                name: p.name === oldLabel ? newLabel : p.name,
              }));
            }}
          />
          <div className={styles.fieldLabel}>
            Duration
            <DurationStepper
              seconds={local.durationSeconds}
              onChange={(s) => set("durationSeconds", s)}
            />
          </div>
          <BellPillRow
            label="Bell before"
            value={local.bellBeforePattern}
            patterns={bellPatterns}
            onChange={(p) => set("bellBeforePattern", p)}
          />
          <BellPillRow
            label="Bell after"
            value={local.bellAfterPattern}
            patterns={bellPatterns}
            onChange={(p) => set("bellAfterPattern", p)}
          />
        </div>
        <div className={styles.sheetFooter}>
          <button
            type="button"
            className={styles.sheetSave}
            onClick={() => { haptic(); onUpdate(local); onClose(); }}
          >
            Done
          </button>
          <button
            type="button"
            className={styles.sheetDelete}
            onClick={() => { haptic(); onDelete(); onClose(); }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ── AddContainerPicker ────────────────────────────────────────────────────────

export type AddContainerPickerProps = {
  containerTypes: ContainerTypeMeta[];
  onPick: (type: ContainerTypeMeta) => void;
  onClose: () => void;
};

export function AddContainerPicker({
  containerTypes,
  onPick,
  onClose,
}: AddContainerPickerProps) {
  return (
    <div className={styles.sheetOverlay} onClick={onClose}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.sheetHandle} />
        <div className={styles.sheetBody}>
          <p className={styles.addPickerTitle}>Add a session</p>
          {containerTypes.map((ct) => (
            <button
              key={ct.type}
              type="button"
              className={styles.typePickerRow}
              onClick={() => { haptic(); onPick(ct); }}
            >
              <span className={styles.typeDot} style={{ background: ct.dotToken }} />
              {ct.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
